import { useEffect, useRef } from 'react';
import { useQuery } from '@apollo/react-hooks';

import type { InlineCommentPluginState } from '@atlaskit/editor-plugin-annotation';

import type {
	InlineCommentMarkerRefLocation,
	InlineCommentMarkerRefQueryType,
} from '@confluence/inline-comments-queries';
import { InlineCommentMarkerRefQuery } from '@confluence/inline-comments-queries';
import { openInlineCommentInEditor } from '@confluence/comments-util';
import { useUnreadComments } from '@confluence/unread-comments';
import { fg } from '@confluence/feature-gating';
import { useShowCommentsPanel } from '@confluence/comments-panel';
import { useCommentsPanelActions, ViewValues } from '@confluence/comments-panel-utils';

type FocusedCommentProps = {
	commentId: string | undefined;
	contentId: string;
	annotationState: InlineCommentPluginState | undefined;
	handleGqlError: (error: Error) => void;
};

export function useFocusedComment({
	commentId,
	contentId,
	annotationState,
	handleGqlError,
}: FocusedCommentProps): void {
	// this is for focused comment
	const { data } = useQuery<InlineCommentMarkerRefQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		InlineCommentMarkerRefQuery,
		{
			variables: {
				commentId,
				pageId: contentId,
				contentStatus: ['DRAFT', 'CURRENT'],
			},
			skip: !commentId,
			onError: handleGqlError,
			fetchPolicy: 'cache-first',
		},
	);
	const { setCurrentlySelectedCommentMarkerRef } = useCommentsPanelActions();
	const { showCommentsPanel } = useShowCommentsPanel();

	const hasOpenedFocusedComment = useRef(false);

	const [{ shouldUpdateUnreadCommentsOnFirstRender }] = useUnreadComments();

	useEffect(() => {
		if (hasOpenedFocusedComment.current) {
			return;
		}
		const focusedComment = data?.comments?.nodes?.[0] ?? null;

		if (
			commentId &&
			focusedComment &&
			(fg('confluence_frontend_content_wrapper') || !shouldUpdateUnreadCommentsOnFirstRender)
		) {
			const markerRef =
				(focusedComment.location as InlineCommentMarkerRefLocation).inlineMarkerRef ?? '';
			const isResolved = Boolean(
				(focusedComment.location as InlineCommentMarkerRefLocation).inlineResolveProperties
					?.resolved,
			);

			if (isResolved && fg('confluence-frontend-comments-panel')) {
				showCommentsPanel({ openWithView: ViewValues.RESOLVED });
				if (markerRef) {
					setCurrentlySelectedCommentMarkerRef(markerRef);
					hasOpenedFocusedComment.current = true;
				}
			} else {
				const commentElement = document.getElementById(markerRef);

				if (commentElement && markerRef !== '') {
					openInlineCommentInEditor(commentElement, markerRef);
					hasOpenedFocusedComment.current = true;
				}
			}
		}
	}, [
		commentId,
		data,
		annotationState?.annotations,
		shouldUpdateUnreadCommentsOnFirstRender,
		showCommentsPanel,
		setCurrentlySelectedCommentMarkerRef,
	]);
}
